/**
 * @documental
 * @export Link
 * @name
 * Table.Link
 */
import { FC, MouseEvent, useCallback } from "react";

import { Link as InternalLink, LinkProps } from "capitalrx-components-2.0/lib/general/Link";

import { BaseColumnProps, ColumnRenderFunction } from "../BaseColumnProps";
import { ColumnElementConstructor } from "../ColumnElementConstructor";
import { useTableRow } from "../useTableRow";
import { Custom } from "./Custom";

/**
 * The `Link` component properties.
 */

export interface LinkColumnProps<TData extends object = object>
  extends Omit<BaseColumnProps, "align">,
    Omit<LinkProps, "className" | "onClick" | "children" | "to"> {
  /**
   * The to address template format function.
   */
  readonly to: ColumnRenderFunction<TData, string>;
  /**
   * The component template.
   */
  readonly children?: ColumnRenderFunction<TData>;
}
/**
 * Column which displays a link using the value as the address.
 */
export const Link: FC<LinkColumnProps> = (props) => {
  const { to, children, className, name, title, initialWidth, minWidth, sortable, ...linkProps } = props;
  const tableRow = useTableRow();

  const clickHandler = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  if (tableRow === null) return null;

  const { data } = tableRow;

  if (data === null) return null;

  const customProps = { className, name, title, initialWidth, minWidth, sortable };

  return (
    <Custom {...customProps}>
      {(value) => {
        if (value === null) return "--";
        if (value === undefined) return "--";

        const toUrl = to(value, tableRow.id, tableRow);
        if (!toUrl || typeof toUrl !== "string") return "--";

        return (
          <InternalLink {...linkProps} to={toUrl} onClick={clickHandler}>
            {children?.(value, tableRow.id, tableRow) ?? "Link"}
          </InternalLink>
        );
      }}
    </Custom>
  );
};

(Link as ColumnElementConstructor<LinkColumnProps>).className = "link";
(Link as ColumnElementConstructor<LinkColumnProps>).headerAlignment = "left";
